/* eslint-disable max-len */
import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import styled from 'styled-components';

import * as ShowActions from '../store/actions/show';
import api, {graphqlQueryBuild} from '../services/api';
import {isMobile, notWebMediaQuery, PageBodyWrapper} from '../styles';
import {slugify} from '../helpers';

import ShowsCarroussel from './../components/ShowsCarroussel';
import Loader from '../components/Loader';
import Spotlights from '../components/Spotlights';
import ContentRow from '../components/ContentRow';
import LiveCarousel from '../components/LiveCarousel';
import {loadHome} from '../store/actions/home';
import {getHomeData, selectPnP} from '../store/reducers';
import * as PnPActions from '../store/actions/pnp';
import * as braze from '@braze/web-sdk';

const Container = styled(PageBodyWrapper)`
    width: 100%;
    min-height: calc(100vh - 392px);
`;

const CarrouselRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    h1 {
        font-size: 3rem;
        text-transform: uppercase;
        margin-top: 0;
        margin-bottom: 2rem;
    }
`;

const Spacer = styled.div`
    height: 16px;

    ${notWebMediaQuery} {
        height: 78px;
    }
`;

const Home = props => {
    const {t} = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const {config, user} = useSelector(state => state);
    const idp = user && user.idp;
    const pnp = useSelector(selectPnP);

    let {pickAndPlace} = config;

    if (!pickAndPlace) {
        pickAndPlace = {
            home: [{}],
            movie: [{}],
        };
    }

    if (!['prod', 'prd'].includes(process.env.REACT_APP_STAGE)) {
        if (localStorage.debug_picknplace) {
            try {
                pickAndPlace = JSON.parse(localStorage.debug_picknplace);
            } catch (error) {}
        }
    }

    props = {
        ...props,
        ...useSelector(state => {
            return {...state.show, ...state.user};
        }),
    };

    const {movies} = useSelector(state => state.movie);

    const [readyState, setReadyState] = useState(false);
    const rows = useSelector(getHomeData);
    const [shows, setShows] = useState(undefined);
    const [sentPageview, setSentPageview] = useState(false);
    const [games, setGames] = useState(undefined);

    useEffect(() => {
        braze.automaticallyShowInAppMessages();

        if (!sentPageview) {
            setSentPageview(true);

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    pageName: 'cnplus.com/home',
                    section: 'Homepage',
                    subsection: 'nvs',
                    contenttype: 'Homepage',
                    contenttitle: 'Homepage',
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: 'cnplus.com',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });
        }

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setReadyState(false);

        if (user.catalogTenant || !user.jwtToken) {
            handleLoadHomeRows();
            handleLoadGames();
            dispatch(PnPActions.loadPnP());
            //dispatch(loadStickers());
        }

        // eslint-disable-next-line
    }, [user.catalogTenant, user.jwtToken]);

    useEffect(() => {
        if (!!rows && (movies !== undefined || !user.jwtToken) && !readyState) {
            setReadyState(true);
        }

        // eslint-disable-next-line
    }, [rows, movies]);

    useEffect(() => {
        (user.catalogTenant || !user.jwtToken) &&
            dispatch(ShowActions.loadShows());
        // eslint-disable-next-line
    }, [user.catalogTenant, user.jwtToken]);

    useEffect(() => {
        if (props.shows) {
            setShows(props.shows);
        }

        // eslint-disable-next-line
    }, [props.shows]);

    const handleLoadHomeRows = async () => {
        dispatch(loadHome());
    };

    const handleLoadGames = async () => {
        let query = graphqlQueryBuild(
            'games',
            `
                id
                title
                description
                url
                orientation
                image
                showName
                internationalCategory
                internationalTitle
                soundIssue
            `,
            '',
            false,
            false,
        );

        let response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (response.data.data && response.data.data.games) {
            const tempGames = response.data.data.games.reduce((acc, game) => {
                game = {...game, metadata: {contentId: game.id}};
                return [...acc, game];
            }, []);

            setGames(tempGames);
        }
    };

    const handleClickEp = (episode_id, item) => {
        history.push({
            pathname: `/play/${episode_id}`,
            state: {item},
        });
    };

    const handleClickGame = (gameId, item) => {
        const gameUrl = `/games/play/${slugify(item.title)}-${gameId}`;

        history.location.pathname !== gameUrl &&
            history.push({
                pathname: gameUrl,
                state: {item},
            });

        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                pageName: 'cnplus.com/home',
                section: 'Homepage',
                subsection: 'nvs',
                contenttype: 'Homepage',
                contenttitle: 'Homepage',
                platform: 'web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                franchise: item.showName,
                englishname: item.internationalTitle,
                gamecategory: item.internationalCategory,
                gametitle: item.title,
                gameclickedevent: 1,
            },
        });
    };

    const rowTitles = {
        recommended: 'recommended',
        releases: 'newReleases',
        row1: 'comedy',
        row2: 'adventure',
    };

    const renderContentRows = React.useMemo(() => {
        if (!rows || !games || !shows) return;

        return (
            <CarrouselRow>
                {rows.map(
                    (row, index) =>
                        row &&
                        row.vods.length > 0 && (
                            <ContentRow
                                key={`k${index}-${rows.title}`}
                                title={t(
                                    `home.${
                                        rowTitles[row.title.toLowerCase()]
                                    }`,
                                )}
                                titleAlign="left"
                                items={row.vods}
                                onClickContent={handleClickEp}
                                sendEvents
                            />
                        ),
                )}
            </CarrouselRow>
        );

        // eslint-disable-next-line
    }, [readyState, rows]);

    const renderPicknPlace = () => {
        if (pnp) {
            //use pnp from graphQL
            return (
                <Spotlights
                    background={isMobile ? pnp.image : pnp.imageWide}
                    title={pnp.title}
                    subtitle={pnp.subtitle}
                    hint={pnp.hint}
                    type={pnp.type}
                    link={pnp.link}
                    id={pnp.id}
                />
            );
        }
        /*if (
            pickAndPlace &&
            pickAndPlace.home &&
            pickAndPlace.home.length > 0 &&
            pickAndPlace.home[0].title
        ) {
            return <Spotlights pickAndPlace={pickAndPlace.home} />;
        } else if (config?.livestream?.length > 0) {
            return (
                <Spotlights
                    background={
                        isMobile
                            ? 'https://cn.i.cdn.ti-platform.com/cnplus/images/live_pp_2x.png'
                            : 'https://cn.i.cdn.ti-platform.com/cnplus/images/live_pp_tablet_2x.png'
                    }
                    title={config.livestream[0].title[getCurrentLocale()]}
                    subtitle={config.livestream[0].subtitle[
                        getCurrentLocale()
                    ].toUpperCase()}
                    type="live"
                    id="freelive1"
                />
            );
        }*/

        return <Spacer />;
    };

    return (
        <>
            <Helmet>
                <title>{t('home.metaTitle')}</title>
                <meta name="description" content={t('home.metaDescription')} />
            </Helmet>

            <Container>
                {/* {!readyState ? ( */}
                <Loader hide={readyState} />
                {/* )} */}
                {readyState && (
                    <>
                        {renderPicknPlace()}

                        <ShowsCarroussel shows={shows} />

                        <LiveCarousel homeLoaded={shows?.length > 0} />

                        {games && (
                            <ContentRow
                                title={t(`home.games`)}
                                titleAlign="left"
                                items={games.slice(0, 10)}
                                onClickContent={handleClickGame}
                                type="games"
                            />
                        )}

                        {renderContentRows}
                    </>
                )}
            </Container>
        </>
    );
};

export default Home;
